import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Starfield from "@/components/Starfield";
import Rocket from "@/components/Rocket";
import Planet from "@/components/Planet";
import ProgressTrack from "@/components/ProgressTrack";
import { joinByCode, addPupil, updatePupil, updateProgress, setActivePupilToken, clearActivePupilToken } from "@/lib/api";
import { containsProfanity } from "@/lib/profanity";
import { fireCelebration, playChime } from "@/lib/celebrate";
import { ROCKET_COLORS, SHIP_VARIANTS } from "@/constants";
import AssessmentTest from "@/components/AssessmentTest";
import ClockDock from "@/components/ClockDock";
import { printCertificate } from "@/lib/certificate";
import { renderToStaticMarkup } from "react-dom/server";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Check, Pencil, UserPlus, Crown, Lock } from "lucide-react";

const pupilKey = (classId) => `starlight_pupil_${classId}`;

export default function StudentPlay() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [error, setError] = useState(false);
  const [myId, setMyId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", color: ROCKET_COLORS[1].hex, variant: "rocket" });
  const [banner, setBanner] = useState(null);
  const [justCode, setJustCode] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const interacting = useRef(false);

  const load = async () => {
    try {
      const data = await joinByCode(code);
      setRoom(data);
      return data;
    } catch {
      setError(true);
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      const data = await load();
      if (data) {
        const saved = localStorage.getItem(pupilKey(data.id));
        if (saved && data.pupils.some((p) => p.id === saved)) {
          setMyId(saved);
          setActivePupilToken(saved);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Live refresh so teacher edits (new stages, moves) show up.
  useEffect(() => {
    const t = setInterval(async () => {
      if (interacting.current || document.hidden) return;
      try {
        const data = await joinByCode(code);
        setRoom(data);
      } catch {}
    }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const me = room?.pupils.find((p) => p.id === myId) || null;
  const teacher = room?.pupils.find((p) => p.isTeacher) || null;

  const pickMe = (id) => {
    setMyId(id);
    localStorage.setItem(pupilKey(room.id), id);
    setActivePupilToken(id);
  };

  const createMe = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Type your name first");
    if (containsProfanity(name)) return toast.error("Please choose a friendlier name 🚀");
    interacting.current = true;
    try {
      const pupil = await addPupil(room.id, form);
      const data = await load();
      pickMe(pupil.id);
      setAdding(false);
      setForm({ name: "", color: ROCKET_COLORS[1].hex, variant: "rocket" });
      if (pupil.code) setJustCode(pupil.code);
      toast.success(`Welcome aboard, ${name}! 🚀`);
    } catch {
      toast.error("Could not join, try again");
    }
    interacting.current = false;
  };

  const saveEdit = async () => {
    if (containsProfanity(form.name)) return toast.error("Please choose a friendlier ship name 🚀");
    interacting.current = true;
    try {
      await updatePupil(room.id, me.id, { name: form.name.trim() || me.name, color: form.color, variant: form.variant });
      await load();
      setEditing(false);
      toast.success("Ship updated!");
    } catch {
      toast.error("Could not save");
    }
    interacting.current = false;
  };

  const onSnap = async (pupil, stageIndex) => {
    if (stageIndex === pupil.stageIndex) return;
    const advanced = stageIndex > pupil.stageIndex;
    interacting.current = true;
    setRoom((r) => ({
      ...r,
      pupils: r.pupils.map((p) => (p.id === pupil.id ? { ...p, stageIndex } : p)),
    }));
    await updateProgress(room.id, pupil.id, stageIndex);
    if (advanced) {
      const isFinish = stageIndex === room.stages.length - 1;
      fireCelebration(isFinish);
      playChime(false);
      setBanner({ stage: room.stages[stageIndex], finish: isFinish });
      setTimeout(() => setBanner(null), 2600);
    }
    setTimeout(() => (interacting.current = false), 300);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0D1B] text-white flex flex-col items-center justify-center gap-4 p-6">
        <p className="font-heading text-2xl">That mission code isn't working.</p>
        <Button onClick={() => navigate("/join")} className="bg-cyan-500 text-[#0B0D1B]">Try another code</Button>
      </div>
    );
  }
  if (!room) {
    return <div className="min-h-screen bg-[#0B0D1B] text-white flex items-center justify-center font-heading text-xl">Boarding…</div>;
  }

  const n = room.stages.length;
  const PAD = 6;
  const stagePct = (i) => (n <= 1 ? PAD : PAD + (i / (n - 1)) * (100 - 2 * PAD));

  // ---- Name picker / ship builder ----
  const showBuilder = !me || adding || editing;
  if (showBuilder) {
    const isEdit = editing && me;
    return (
      <div className="relative min-h-screen bg-[#0B0D1B] text-white overflow-hidden">
        <Starfield />
        <div className="relative z-10 max-w-lg mx-auto p-5">
          <button onClick={() => navigate("/join")} className="text-slate-400 hover:text-white flex items-center gap-1 text-sm mb-4" data-testid="student-back-btn">
            <ArrowLeft className="w-4 h-4" /> {room.name}
          </button>

          {!me && !adding && (
            <>
              <h1 className="font-heading text-3xl font-bold mb-1">Build your ship</h1>
              {room.teacherName && (
                <p data-testid="student-teacher-name" className="text-amber-300 font-heading mb-1">👑 {room.teacherName}'s class</p>
              )}
              <p className="text-slate-400 mb-5">First time here? Create your rocket to join the crew.</p>
              <Button data-testid="add-me-btn" onClick={() => setAdding(true)} className="w-full bg-pink-500 hover:bg-pink-600 min-h-[52px] font-heading font-bold rounded-2xl">
                <UserPlus className="w-5 h-5 mr-2" /> Build my ship
              </Button>
              <p className="text-xs text-slate-500 mt-4">Already have a pupil code? Go back and tap "Returning Pupil".</p>
            </>
          )}

          {(adding || isEdit) && (
            <>
              <h1 className="font-heading text-3xl font-bold mb-4">{isEdit ? "Edit your ship" : "Build your ship"}</h1>
              <div className="flex justify-center mb-4">
                <div className="hover-bob">
                  <Rocket color={form.color} variant={form.variant} size={110} />
                </div>
              </div>
              <label className="text-xs uppercase tracking-wider text-cyan-400 font-semibold">Your name</label>
              <Input
                data-testid="student-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={isEdit ? me.name : "Type your name"}
                className="bg-[#141738] border-[#252B5C] text-white min-h-[52px] text-lg mt-1 mb-4"
                autoFocus
              />
              <label className="text-xs uppercase tracking-wider text-cyan-400 font-semibold">Ship</label>
              <div className="grid grid-cols-5 gap-2 mt-1 mb-4" data-testid="ship-variant-picker">
                {SHIP_VARIANTS.map((v) => (
                  <button
                    key={v.id}
                    data-testid={`variant-${v.id}`}
                    onClick={() => setForm((f) => ({ ...f, variant: v.id }))}
                    title={v.name}
                    className={`rounded-xl p-1 border-2 transition-all ${form.variant === v.id ? "border-cyan-400 bg-cyan-400/10" : "border-[#252B5C] bg-[#141738]"}`}
                  >
                    <Rocket color={form.color} variant={v.id} size={44} flame={false} />
                  </button>
                ))}
              </div>
              <label className="text-xs uppercase tracking-wider text-cyan-400 font-semibold">Colour</label>
              <div className="flex flex-wrap gap-2 mt-1 mb-6" data-testid="color-picker">
                {ROCKET_COLORS.map((c) => (
                  <button
                    key={c.hex}
                    onClick={() => setForm((f) => ({ ...f, color: c.hex }))}
                    className={`w-10 h-10 rounded-full border-2 transition-transform hover:scale-110 ${form.color === c.hex ? "border-white scale-110" : "border-transparent"}`}
                    style={{ backgroundColor: c.hex }}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => (isEdit ? setEditing(false) : setAdding(false))}
                  variant="outline"
                  className="flex-1 border-[#252B5C] bg-transparent text-white hover:bg-[#1C204B] min-h-[52px]"
                >
                  Cancel
                </Button>
                <Button
                  data-testid={isEdit ? "save-ship-btn" : "join-crew-btn"}
                  onClick={isEdit ? saveEdit : createMe}
                  className="flex-[2] bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[52px]"
                >
                  <Check className="w-5 h-5 mr-2" /> {isEdit ? "Save" : "Launch me!"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ---- My board ----
  const activeProject = (room.projects || []).find((p) => p.id === room.activeProjectId);
  const accent = activeProject?.color || "#3B82F6";
  const hasTest = (activeProject?.assessment || []).length > 0;
  const atGalaxy = me.stageIndex === n - 1;
  const myProg = (me.projects || {})[room.activeProjectId] || {};
  const myResult = myProg.assessment;
  const attemptsAllowed = activeProject?.attemptsAllowed || 0;
  const attemptsUsed = myResult?.attempts || 0;
  const noAttemptsLeft = attemptsAllowed > 0 && attemptsUsed >= attemptsAllowed && !myResult?.passed;
  const certEligible = myProg.certificateEligible;
  return (
    <div data-testid="student-board" className="relative min-h-screen bg-[#0B0D1B] text-white overflow-hidden">
      <Starfield />
      <div className="relative z-10 max-w-3xl mx-auto p-4 sm:p-6">
        <header className="flex items-center justify-between gap-3 mb-6">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-cyan-400 font-semibold truncate">
              {room.name}{room.projects && room.projects.length > 1 ? ` • ${(room.projects.find((p) => p.id === room.activeProjectId) || {}).name || ""}` : ""}
            </p>
            <h1 className="font-heading text-2xl font-bold truncate flex items-center gap-2">
              <Rocket color={me.color} variant={me.variant} size={34} flame={false} /> {me.name}
            </h1>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="edit-my-ship-btn"
              onClick={() => { setForm({ name: me.name, color: me.color, variant: me.variant }); setEditing(true); }}
              variant="outline" size="sm"
              className="border-[#252B5C] bg-transparent text-white hover:bg-[#1C204B]"
            >
              <Pencil className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Ship</span>
            </Button>
            <Button
              data-testid="switch-pupil-btn"
              onClick={() => { localStorage.removeItem(pupilKey(room.id)); clearActivePupilToken(); setMyId(null); }}
              variant="outline" size="sm"
              className="border-[#252B5C] bg-transparent text-slate-300 hover:bg-[#1C204B]"
            >
              Not me
            </Button>
          </div>
        </header>

        {/* planets */}
        <div className="relative h-20 sm:h-24 mb-3" data-testid="student-stage-header">
          {room.stages.map((s, i) => (
            <div
              key={i}
              className="absolute top-0 flex flex-col items-center"
              style={{ left: `calc(${stagePct(i)}% )`, transform: "translateX(-50%)" }}
            >
              <Planet index={i} total={n} color={accent} planet={s.planet} size={i === me.stageIndex ? 58 : 46} />
              {i === n - 1 && hasTest && atGalaxy && (
                <button
                  data-testid="galaxy-test-popup"
                  onClick={() => setTestOpen(true)}
                  className="celebrate-pop absolute -top-10 whitespace-nowrap bg-[#141738] border-2 border-cyan-400 rounded-full px-3 py-1 text-xs font-heading font-bold text-cyan-200 shadow-[0_0_18px_rgba(0,240,255,0.6)]"
                >
                  {myResult ? `${myResult.percent}% ${myResult.passed ? "✅" : noAttemptsLeft ? "🔒" : "🔁"}` : "🪐 Exploration Test!"}
                </button>
              )}
              <span className={`mt-1 text-[10px] sm:text-xs font-heading font-semibold text-center max-w-[70px] leading-tight truncate-2 ${i === me.stageIndex ? "text-cyan-300" : "text-slate-400"}`}>
                {s.name}
              </span>
            </div>
          ))}
        </div>

        <ProgressTrack
          pupil={me}
          stages={room.stages}
          onSnap={onSnap}
          shipSize={64}
          tall
          markerIndex={teacher && !me.isTeacher ? teacher.stageIndex : null}
        />

        <p className="text-center text-slate-300 mt-4 font-heading">
          You're at: <span className="text-cyan-300">{room.stages[me.stageIndex]?.name}</span>
        </p>
        <p className="text-center text-xs text-slate-500 mt-1">
          Drag your ship, or tap a planet to fly there. 🚀
        </p>

        {certEligible && (
          <div className="text-center mt-5">
            <Button
              data-testid="get-certificate-btn"
              onClick={() => printCertificate({ pupilName: me.name, projectName: activeProject.name, className: room.name, color: accent, percent: myResult.percent, rocketSvg: renderToStaticMarkup(<Rocket color={me.color} variant={me.variant} size={130} flame={false} />) })}
              className="font-heading font-bold text-[#0B0D1B] min-h-[52px] px-6 rounded-2xl cert-glow"
              style={{ backgroundColor: accent, boxShadow: `0 0 26px ${accent}88` }}
            >
              🏆 Get your Certificate
            </Button>
          </div>
        )}
      </div>

      <ClockDock accent={accent} />

      {justCode && (
        <div data-testid="pupil-code-overlay" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setJustCode(null)}>
          <div className="bg-[#141738] border border-[#252B5C] rounded-3xl p-7 text-center max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-2xl font-bold text-cyan-300 mb-1">Your secret code 🔑</h2>
            <p className="text-slate-400 text-sm mb-3">Write this down! Type it under "Returning Pupil" to hop straight back in — no class code needed.</p>
            <div data-testid="pupil-code-value" className="font-heading font-bold text-5xl tracking-[0.3em] text-cyan-300 bg-[#0E1126] border-2 border-cyan-400/40 rounded-2xl py-6 mb-4">{justCode}</div>
            <Button data-testid="pupil-code-got-it" onClick={() => setJustCode(null)} className="w-full bg-cyan-500 hover:bg-cyan-600 text-[#0B0D1B] font-heading font-bold min-h-[48px]">Got it!</Button>
          </div>
        </div>
      )}

      {banner && (
        <div data-testid="student-celebration" className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="celebrate-pop bg-[#141738]/95 backdrop-blur-xl border-2 rounded-3xl px-8 py-6 text-center"
            style={{ borderColor: banner.stage.color, boxShadow: `0 0 40px ${banner.stage.color}` }}>
            <div className="text-5xl mb-2">{banner.finish ? "🏆🎉" : "🚀"}</div>
            <p className="font-heading text-2xl font-bold" style={{ color: banner.stage.color }}>
              {banner.finish ? "You finished!" : `Reached ${banner.stage.name}!`}
            </p>
          </div>
        </div>
      )}
      {activeProject && (
        <AssessmentTest
          open={testOpen}
          onOpenChange={setTestOpen}
          project={activeProject}
          classId={room.id}
          pupilId={me.id}
          myResult={myResult}
          onDone={async () => { const d = await joinByCode(code); setRoom(d); }}
        />
      )}
    </div>
  );
}
